import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useQuery } from '@tanstack/react-query';
import { MarketingService } from '@/services/marketing.service';
import { CampaignStatus } from '@/models/marketing.model';
import { format } from 'date-fns';
import { ContentManagement } from './marketing/ContentManagement';
import { MarketingAnalytics } from './marketing/MarketingAnalytics';
import { WebsiteAnalytics } from './marketing/WebsiteAnalytics';
import { GoogleAdsManagement } from './marketing/GoogleAdsManagement';
import { ConversionFunnel } from './marketing/ConversionFunnel';

export const Marketing: React.FC = () => {
  const { profile } = useAuth();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<'campaigns' | 'emails' | 'content' | 'analytics' | 'events' | 'website' | 'google-ads' | 'funnel'>('campaigns');

  // Check URL parameters to set active tab
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab && ['campaigns', 'emails', 'content', 'analytics', 'events', 'website', 'google-ads', 'funnel'].includes(tab)) {
      setActiveTab(tab as typeof activeTab);
    }
  }, [searchParams]);

  const { data: campaigns = [], isLoading: campaignsLoading } = useQuery({
    queryKey: ['marketingCampaigns'],
    queryFn: () => MarketingService.getAllCampaigns(),
    enabled: !!profile && activeTab === 'campaigns',
  });

  const { data: emailCampaigns = [], isLoading: emailsLoading } = useQuery({
    queryKey: ['emailCampaigns'],
    queryFn: () => MarketingService.getAllEmailCampaigns(),
    enabled: !!profile && activeTab === 'emails',
  });

  const { data: events = [], isLoading: eventsLoading } = useQuery({
    queryKey: ['marketingEvents'],
    queryFn: () => MarketingService.getAllEvents(),
    enabled: !!profile && activeTab === 'events',
  });

  const tabs = [
    { id: 'campaigns' as const, name: 'Campaigns', count: campaigns.length },
    { id: 'emails' as const, name: 'Email Campaigns', count: emailCampaigns.length },
    { id: 'website' as const, name: 'Website Visitors', count: 0 },
    { id: 'google-ads' as const, name: 'Google Ads', count: 0 },
    { id: 'funnel' as const, name: 'Conversion Funnel', count: 0 },
    { id: 'content' as const, name: 'Content', count: 0 },
    { id: 'analytics' as const, name: 'Marketing Analytics', count: 0 },
    { id: 'events' as const, name: 'Events', count: events.length },
  ];

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      draft: 'bg-gray-100 text-gray-800',
      scheduled: 'bg-blue-100 text-blue-800',
      active: 'bg-green-100 text-green-800',
      paused: 'bg-yellow-100 text-yellow-800',
      completed: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
      sent: 'bg-green-100 text-green-800',
      sending: 'bg-yellow-100 text-yellow-800',
      failed: 'bg-red-100 text-red-800',
      planned: 'bg-blue-100 text-blue-800',
      in_progress: 'bg-yellow-100 text-yellow-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  // Calculate stats
  const activeCampaigns = campaigns.filter(c => c.status === CampaignStatus.active).length;
  const totalSpent = campaigns.reduce((sum, c) => sum + (c.spent || 0), 0);
  const totalLeads = campaigns.reduce((sum, c) => sum + (c.metrics?.leads || 0), 0);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Marketing Management</h1>
        <p className="mt-2 text-gray-600">Manage campaigns, email marketing, content, and events</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600">Total Campaigns</div>
          <div className="text-2xl font-bold text-gray-900">{campaigns.length}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600">Active Campaigns</div>
          <div className="text-2xl font-bold text-green-600">{activeCampaigns}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600">Total Spent</div>
          <div className="text-2xl font-bold text-blue-600">${totalSpent.toLocaleString()}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600">Total Leads</div>
          <div className="text-2xl font-bold text-purple-600">{totalLeads}</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`
                py-4 px-1 border-b-2 font-medium text-sm
                ${activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }
              `}
            >
              {tab.name}
              {tab.count > 0 && (
                <span className="ml-2 bg-gray-100 text-gray-900 py-0.5 px-2.5 rounded-full text-xs">
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Campaigns Tab */}
      {activeTab === 'campaigns' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Marketing Campaigns</h2>
            <Link
              to="/marketing/campaigns/new"
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 text-sm font-medium"
            >
              + Create Campaign
            </Link>
          </div>

          {campaignsLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 text-gray-600">Loading campaigns...</p>
            </div>
          ) : campaigns.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-8 text-center">
              <p className="text-gray-600">No campaigns found</p>
            </div>
          ) : (
            <div className="bg-white shadow overflow-hidden sm:rounded-md">
              <ul className="divide-y divide-gray-200">
                {campaigns.map((campaign) => (
                  <li key={campaign.id}>
                    <Link
                      to={`/marketing/campaigns/${campaign.id}`}
                      className="block hover:bg-gray-50 px-6 py-4"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center">
                            <p className="text-sm font-medium text-gray-900">
                              {campaign.name}
                            </p>
                            <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(campaign.status)}`}>
                              {campaign.status}
                            </span>
                            <span className="ml-2 text-xs text-gray-500">
                              {campaign.type}
                            </span>
                          </div>
                          <p className="mt-1 text-sm text-gray-500">
                            {campaign.description}
                            {campaign.startDate && ` • ${format(campaign.startDate, 'MMM d, yyyy')}`}
                            {campaign.metrics?.leads && ` • ${campaign.metrics.leads} leads`}
                          </p>
                        </div>
                        <div className="text-right">
                          {campaign.budget && (
                            <p className="text-sm font-medium text-gray-900">
                              Budget: ${campaign.budget.toLocaleString()}
                            </p>
                          )}
                            {(campaign.spent ?? 0) > 0 && (
                              <p className="text-sm text-gray-600">
                                Spent: ${(campaign.spent ?? 0).toLocaleString()}
                              </p>
                            )}
                        </div>
                      </div>
                    </Link>
                    </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Email Campaigns Tab */}
      {activeTab === 'emails' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Email Campaigns</h2>
            <button className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 text-sm font-medium">
              + Create Email Campaign
            </button>
          </div>

          {emailsLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 text-gray-600">Loading email campaigns...</p>
            </div>
          ) : emailCampaigns.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-8 text-center">
              <p className="text-gray-600">No email campaigns found</p>
            </div>
          ) : (
            <div className="bg-white shadow overflow-hidden sm:rounded-md">
              <ul className="divide-y divide-gray-200">
                {emailCampaigns.map((email) => (
                  <li key={email.id}>
                    <div className="block hover:bg-gray-50 px-6 py-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center">
                            <p className="text-sm font-medium text-gray-900">
                              {email.name}
                            </p>
                            <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(email.status)}`}>
                              {email.status}
                            </span>
                          </div>
                          <p className="mt-1 text-sm text-gray-500">
                            {email.subject} • {email.recipientCount} recipients
                            {email.sentAt && ` • Sent: ${format(email.sentAt, 'MMM d, yyyy')}`}
                          </p>
                        </div>
                        <div className="text-right">
                          {(email.openedCount ?? 0) > 0 && (
                            <p className="text-sm text-gray-600">
                              Opens: {email.openedCount ?? 0} ({email.recipientCount > 0 ? Math.round(((email.openedCount ?? 0) / email.recipientCount) * 100) : 0}%)
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Content Tab */}
      {activeTab === 'content' && (
        <ContentManagement />
      )}

      {/* Website Analytics Tab */}
      {activeTab === 'website' && (
        <WebsiteAnalytics />
      )}

      {/* Google Ads Tab */}
      {activeTab === 'google-ads' && (
        <GoogleAdsManagement />
      )}

      {/* Conversion Funnel Tab */}
      {activeTab === 'funnel' && (
        <ConversionFunnel />
      )}

      {/* Marketing Analytics Tab */}
      {activeTab === 'analytics' && (
        <MarketingAnalytics />
      )}

      {/* Events Tab */}
      {activeTab === 'events' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Events & Webinars</h2>
            <Link
              to="/marketing/events/new"
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 text-sm font-medium"
            >
              + Create Event
            </Link>
          </div>

          {eventsLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 text-gray-600">Loading events...</p>
            </div>
          ) : events.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-8 text-center">
              <p className="text-gray-600">No events found</p>
            </div>
          ) : (
            <div className="bg-white shadow overflow-hidden sm:rounded-md">
              <ul className="divide-y divide-gray-200">
                {events.map((event) => (
                  <li key={event.id}>
                    <div className="block hover:bg-gray-50 px-6 py-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center">
                            <p className="text-sm font-medium text-gray-900">
                              {event.name}
                            </p>
                            <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(event.status)}`}>
                              {event.status}
                            </span>
                          </div>
                          <p className="mt-1 text-sm text-gray-500">
                            {event.type} • {format(event.startDate, 'MMM d, yyyy')}
                            {event.location && ` • ${event.location}`}
                            {(event.registrations ?? 0) > 0 && ` • ${event.registrations} registrations`}
                          </p>
                        </div>
                        <div className="text-right">
                          {(event.leads ?? 0) > 0 && (
                            <p className="text-sm font-medium text-gray-900">
                              {event.leads} leads
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

